/**
 * AiCloud Work Projects - Is projeleri (public/private, auto islem)
 * Teklif bazli degil - sistem otomatik agent atar
 */

import axios from 'axios'
import { AlertCircle, Eye, FolderOpen, Lock, Plus, Unlock } from 'lucide-react'
import { useEffect, useState } from 'react'
import { API_URL } from '../../../config/api'

interface WorkProject {
  id: number
  title: string
  description: string
  is_private: boolean
  status: string
  agent_count: number
  task_count: number
  completed_tasks: number
  created_at: string
}

interface CreateForm {
  title: string
  description: string
  is_private: boolean
}

export default function WorkProjectsPage() {
  const [projects, setProjects] = useState<WorkProject[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState<CreateForm>({ title: '', description: '', is_private: false })

  const fetchProjects = async () => {
    try {
      const token = localStorage.getItem('token')
      const resp = await axios.get(`${API_URL}/api/projects/my?type=work`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      const data = resp.data as { projects?: WorkProject[] } | WorkProject[]
      setProjects(Array.isArray(data) ? data : (data.projects ?? []))
    } catch {
      setProjects([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { void fetchProjects() }, [])

  const handleCreate = async () => {
    if (!form.title.trim()) {
      setError('Project title is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const token = localStorage.getItem('token')
      await axios.post(`${API_URL}/api/projects`, {
        ...form,
        project_type: 'work'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      })
      setShowCreate(false)
      setForm({ title: '', description: '', is_private: false })
      void fetchProjects()
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } } }
      setError(err.response?.data?.detail ?? 'Failed to create project')
    } finally {
      setSaving(false)
    }
  }

  const statusColor: Record<string, string> = {
    active: '#10b981',
    completed: '#3b82f6',
    paused: '#f59e0b',
    draft: '#6b7280'
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div>
          <h2 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '4px' }}>Work Projects</h2>
          <p style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
            Your work projects. Can be public or private. Agents are assigned automatically.
          </p>
        </div>
        <button onClick={() => setShowCreate(!showCreate)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
          <Plus size={14} /> New Project
        </button>
      </div>

      {error && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', color: '#ef4444', fontSize: '13px', marginBottom: '16px' }}>
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {showCreate && (
        <div style={{ padding: '20px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: 600, marginBottom: '16px' }}>New Work Project</h3>
          <div style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Title</label>
            <input type="text" placeholder="Project title" value={form.title}
              onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
              style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px' }} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '6px' }}>Description</label>
            <textarea placeholder="What does this project do?" value={form.description}
              onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
              rows={3}
              style={{ width: '100%', padding: '8px 10px', background: 'var(--body-bg)', border: '1px solid var(--border-color)', borderRadius: '6px', color: 'var(--text-primary)', fontSize: '13px', resize: 'vertical' }} />
          </div>
          {/* Gizlilik secimi */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            {[
              { value: false, icon: Unlock, label: 'Public', desc: 'Visible to everyone' },
              { value: true, icon: Lock, label: 'Private', desc: 'Only you can see it' },
            ].map(opt => (
              <button key={String(opt.value)}
                onClick={() => setForm(p => ({ ...p, is_private: opt.value }))}
                style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '10px', padding: '12px', background: form.is_private === opt.value ? 'rgba(59,130,246,0.1)' : 'var(--body-bg)', border: `1px solid ${form.is_private === opt.value ? '#3b82f6' : 'var(--border-color)'}`, borderRadius: '8px', cursor: 'pointer', textAlign: 'left' }}>
                <opt.icon size={16} color={form.is_private === opt.value ? '#3b82f6' : 'var(--text-secondary)'} />
                <div>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: form.is_private === opt.value ? '#3b82f6' : 'var(--text-primary)' }}>{opt.label}</div>
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{opt.desc}</div>
                </div>
              </button>
            ))}
          </div>
          <div style={{ padding: '10px 12px', background: 'rgba(59,130,246,0.05)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '6px', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Agents will be automatically assigned to this project based on your AiCloud configuration.
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button onClick={() => { void handleCreate() }} disabled={saving}
              style={{ padding: '8px 16px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '6px', cursor: saving ? 'not-allowed' : 'pointer', fontSize: '13px', fontWeight: 600, opacity: saving ? 0.6 : 1 }}>
              {saving ? 'Creating...' : 'Create Project'}
            </button>
            <button onClick={() => setShowCreate(false)}
              style={{ padding: '8px 16px', background: 'transparent', color: 'var(--text-secondary)', border: '1px solid var(--border-color)', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Loading...</div>
      ) : projects.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', background: 'var(--card-bg)', border: '1px dashed var(--border-color)', borderRadius: '10px' }}>
          <FolderOpen size={32} color="#3b82f6" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>No work projects yet.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {projects.map(p => (
            <div key={p.id} style={{ padding: '16px', background: 'var(--card-bg)', border: '1px solid var(--border-color)', borderRadius: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {p.is_private ? <Lock size={14} color="var(--text-secondary)" /> : <Eye size={14} color="var(--text-secondary)" />}
                  <span style={{ fontSize: '14px', fontWeight: 600 }}>{p.title}</span>
                </div>
                <span style={{ fontSize: '11px', padding: '2px 8px', background: `${statusColor[p.status] ?? '#6b7280'}20`, color: statusColor[p.status] ?? '#6b7280', borderRadius: '4px', fontWeight: 600 }}>
                  {p.status}
                </span>
              </div>
              {p.description && (
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '10px' }}>{p.description}</p>
              )}
              <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                <span>{p.agent_count} agents</span>
                <span>{p.completed_tasks}/{p.task_count} tasks</span>
                <span>{p.is_private ? 'Private' : 'Public'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
